import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Copy, CheckCircle, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

export default function ShareModal({ open, onOpenChange, url, snapshot }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("share.toast.copied.title"), {
        description: t("share.toast.copied.desc"),
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard", {
        description: "Long-press the field to copy manually.",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="sim-share-modal"
        className="max-w-lg bg-slate-950/95 border-white/10 text-white backdrop-blur-xl"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-400">
            <LinkSimple weight="bold" className="h-3 w-3" /> {t("share.eyebrow")}
          </div>
          <DialogTitle className="text-2xl font-display font-semibold mt-2">
            {t("share.title")}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {t("share.desc")}
          </DialogDescription>
        </DialogHeader>

        {snapshot && (
          <div
            className="mt-2 grid grid-cols-3 gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]"
            data-testid="sim-share-snapshot"
          >
            <Metric label={t("share.snapshot.youEarn")} value={snapshot.youEarn} tone="grad" />
            <Metric label={t("share.snapshot.teamPayout")} value={snapshot.teamPayout} tone="emerald" />
            <Metric label={t("share.snapshot.rank")} value={snapshot.rank} tone="slate" />
          </div>
        )}

        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-widest text-slate-500">
            {t("share.linkLabel")}
          </label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              data-testid="sim-share-url-input"
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 rounded-lg bg-white/[0.05] border border-white/[0.08] px-3 py-2 text-xs font-mono text-slate-200 focus:outline-none focus:ring-1 focus:ring-pink-500/50 truncate"
            />
            <button
              data-testid="sim-share-copy"
              onClick={doCopy}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all ${
                copied
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
                  : "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg shadow-pink-500/30 hover:brightness-110"
              }`}
            >
              {copied ? (
                <>
                  <CheckCircle weight="bold" className="h-4 w-4" /> {t("share.copied")}
                </>
              ) : (
                <>
                  <Copy weight="bold" className="h-4 w-4" /> {t("share.copy")}
                </>
              )}
            </button>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            {t("share.footer")}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, tone }) {
  const cls =
    tone === "grad"
      ? "grad-text"
      : tone === "emerald"
      ? "text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-200"
      : "text-slate-200";
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className={`mt-1 font-mono text-sm font-semibold ${cls}`}>
        {value}
      </div>
    </div>
  );
}
